<template>
  <div class="info">
    <div class="data-report-container">
      <!-- 头部标题 -->
      <div class="header">
        <h2 class="title">数据上报平台</h2>
        <p class="subtitle">支持单条数据上报和Excel批量上报</p>
      </div>
  
      <!-- 选项卡切换 -->
      <el-tabs v-model="activeTab" class="tabs-container">
        <!-- 单条上报选项卡 -->
        <el-tab-pane label="单条上报" name="single">
          <div class="single-report">
            <el-form class="report-form" label-position="left" label-width="100px">
              <el-form-item label="adid">
                <el-input 
                  v-model="singleData.adid" 
                  placeholder="请输入adid"
                  clearable
                ></el-input>
              </el-form-item>
              
              <el-form-item label="requestid">
                <el-input 
                  v-model="singleData.requestid" 
                  placeholder="请输入requestid"
                  clearable
                ></el-input>
              </el-form-item>
              
              <el-form-item label="上报类型">
                <el-select 
                  v-model="singleData.cvType" 
                  placeholder="请选择上报类型"
                  class="type-select"
                >
                  <el-option
                    v-for="item in reportTypes"
                    :key="item.value"
                    :label="item.label"
                    :value="item.value"
                  ></el-option>
                </el-select>
              </el-form-item>
              
              <el-form-item>
                <el-button 
                  type="primary" 
                  :loading="singleLoading"
                  @click="handleSingleSubmit"
                  class="submit-btn"
                >
                  {{ singleLoading ? '上报中...' : '单条上报' }}
                </el-button>
              </el-form-item>
            </el-form>
          </div>
        </el-tab-pane>
  
        <!-- 批量上报选项卡 -->
        <el-tab-pane label="批量上报" name="batch">
          <div class="batch-report">
            <!-- 文件上传区域 -->
            <div class="upload-section">
              <el-upload
                class="upload-demo"
                drag
                :auto-upload="false"
                :on-change="handleFileChange"
                :show-file-list="false"
                action="#"
                accept=".xlsx,.xls"
              >
                <i class="el-icon-upload"></i>
                <div class="el-upload__text">
                  将Excel文件拖到此处，或<em>点击上传</em>
                </div>
                <div class="el-upload__tip" slot="tip">
                  只能上传xlsx/xls文件，且不超过10MB
                </div>
              </el-upload>
              
              <div v-if="uploadedFileName" class="file-info">
                <el-tag type="success">
                  <i class="el-icon-document"></i>
                  {{ uploadedFileName }}
                </el-tag>
                <span class="data-count">共 {{ parsedData.length }} 条数据</span>
              </div>
            </div>
  
            <!-- 数据类型选择 -->
            <div class="type-select-section">
              <el-form label-position="left" label-width="100px">
                <el-form-item label="上报类型">
                  <el-select 
                    v-model="batchData.cvType" 
                    placeholder="请选择上报类型"
                    class="type-select"
                  >
                    <el-option
                      v-for="item in reportTypes"
                      :key="item.value"
                      :label="item.label"
                      :value="item.value"
                    ></el-option>
                  </el-select>
                </el-form-item>
              </el-form>
            </div>
  
            <!-- 数据预览 -->
            <div v-if="parsedData.length" class="data-preview">
              <h4>数据预览 (前10条)</h4>
              <el-table 
                :data="parsedData.slice(0, 10)" 
                border 
                stripe
                height="200"
              >
                <el-table-column prop="adid" label="adid" width="180"></el-table-column>
                <el-table-column prop="requestid" label="requestid" width="220"></el-table-column>
                <el-table-column label="状态" width="100">
                  <template #default="{ $index }">
                    <el-tag 
                      :type="getBatchStatus($index)" 
                      size="small"
                    >
                      {{ getBatchStatusText($index) }}
                    </el-tag>
                  </template>
                </el-table-column>
              </el-table>
            </div>
  
            <!-- 操作按钮 -->
            <div class="batch-actions">
              <el-button 
                type="primary" 
                :loading="batchLoading"
                :disabled="!parsedData.length || !batchData.cvType"
                @click="handleBatchSubmit"
                class="batch-btn"
              >
                {{ batchLoading ? '批量上报中...' : `批量上报 (${parsedData.length}条)` }}
              </el-button>
              
              <el-button 
                @click="clearBatchData"
                :disabled="!parsedData.length"
              >
                清空数据
              </el-button>
            </div>
  
            <!-- 进度条 -->
            <div v-if="batchLoading" class="progress-section">
              <el-progress 
                :percentage="progressPercentage" 
                :status="progressStatus"
                :text-inside="true"
                :stroke-width="20"
              ></el-progress>
              <p class="progress-text">
                已处理: {{ processedCount }}/{{ parsedData.length }}
              </p>
            </div>
          </div>
        </el-tab-pane>
      </el-tabs>
  
      <!-- 结果统计 -->
      <div v-if="resultStats.total > 0" class="result-stats">
        <el-card shadow="never">
          <h4>上报结果统计</h4>
          <div class="stats-content">
            <div class="stat-item">
              <span class="stat-label">总数:</span>
              <span class="stat-value">{{ resultStats.total }}</span>
            </div>
            <div class="stat-item">
              <span class="stat-label">成功:</span>
              <span class="stat-value success">{{ resultStats.success }}</span>
            </div>
            <div class="stat-item">
              <span class="stat-label">失败:</span>
              <span class="stat-value error">{{ resultStats.failed }}</span>
            </div>
          </div>
        </el-card>
      </div>
    </div>

  </div>
    
  </template>
  
  <script>
  import * as XLSX from 'xlsx';
  
  export default {
    name: 'DataReportPlatform',
    data() {
      return {
        activeTab: 'single',
        
        // 单条上报数据
        singleData: {
          adid: '',
          requestid: '',
          cvType: ''
        },
        singleLoading: false,
        
        // 批量上报数据
        batchData: {
          cvType: ''
        },
        batchLoading: false,
        uploadedFileName: '',
        parsedData: [],
        batchResults: [], // 存储每条数据的上报结果
        
        // 进度控制
        processedCount: 0,
        currentProcessingIndex: -1,
        
        // 上报类型选项
        reportTypes: [
          { value: 'SUBMIT', label: '表单提交' },
          { value: 'WEBPAY', label: '网页购买' },
          { value: 'REACTIVATION', label: '自定义拉活' },
          { value: 'ACTIVATION', label: '自定义激活' },
          { value: 'PAY', label: '自定义付费' },
          { value: 'RETENTION_1', label: '自定义次留' },
          { value: 'ADD_SCREEN', label: '加桌' },
          { value: 'DIALOGUE_MPA', label: '微信-用户首次消息' },
          { value: 'ADD_WECHAT', label: '微信-添加微信' },
          { value: 'REGISTER', label: '自定义注册' }
        ],
        
        // 结果统计
        resultStats: {
          total: 0,
          success: 0,
          failed: 0
        }
      };
    },
    computed: {
      // 计算进度百分比
      progressPercentage() {
        if (this.parsedData.length === 0) return 0;
        return Math.round((this.processedCount / this.parsedData.length) * 100);
      },
      
      // 进度条状态
      progressStatus() {
        return this.progressPercentage === 100 ? 'success' : '';
      }
    },
    methods: {
      // 单条上报
      async handleSingleSubmit() {
        if (!this.singleData.adid || !this.singleData.requestid || !this.singleData.cvType) {
          this.$message.warning('请填写完整信息');
          return;
        }
        
        this.singleLoading = true;
        try {
          await this.sendRequest({
            creativeId: this.singleData.adid,
            requestId: this.singleData.requestid,
            cvType: this.singleData.cvType
          });
          
          this.$message.success('单条上报成功');
          this.singleData = { adid: '', requestid: '', cvType: '' };
        } catch (error) {
          console.error('单条上报失败:', error);
        } finally {
          this.singleLoading = false;
        }
      },
      
      // 处理文件上传
      handleFileChange(file) {
        this.uploadedFileName = file.name;
        
        const reader = new FileReader();
        reader.onload = (e) => {
          try {
            const data = new Uint8Array(e.target.result);
            const workbook = XLSX.read(data, { type: 'array' });
            const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
            const jsonData = XLSX.utils.sheet_to_json(firstSheet, { header: 1 });
            
            this.parsedData = jsonData.slice(1).map((row, index) => {
              const url = row[0];
              return {
                adid: this.extractParam(url, 'adid'),
                requestid: this.extractParam(url, 'requestid'),
                index: index,
                status: 'pending' // pending, success, error
              };
            }).filter(item => item.adid && item.requestid);
            
            this.batchResults = new Array(this.parsedData.length).fill(null);
            this.$message.success(`成功解析 ${this.parsedData.length} 条数据`);
          } catch (error) {
            this.$message.error('文件解析失败');
            console.error('解析错误:', error);
          }
        };
        
        reader.readAsArrayBuffer(file.raw);
      },
      
      // 提取URL参数
      extractParam(url, param) {
        try {
          const urlObj = new URL(url);
          return urlObj.searchParams.get(param);
        } catch (e) {
          return null;
        }
      },
      
      // 批量上报
      async handleBatchSubmit() {
        if (this.parsedData.length === 0) {
          this.$message.warning('请先上传Excel文件');
          return;
        }
        
        if (!this.batchData.cvType) {
          this.$message.warning('请选择上报类型');
          return;
        }
        
        this.batchLoading = true;
        this.processedCount = 0;
        this.currentProcessingIndex = -1;
        this.resultStats = { total: this.parsedData.length, success: 0, failed: 0 };
        
        // 重置状态
        this.parsedData = this.parsedData.map(item => ({ ...item, status: 'pending' }));
        this.batchResults = new Array(this.parsedData.length).fill(null);
        
        for (let i = 0; i < this.parsedData.length; i++) {
          this.currentProcessingIndex = i;
          const item = this.parsedData[i];
          
          try {
            await this.sendRequest({
              creativeId: item.adid,
              requestId: item.requestid,
              cvType: this.batchData.cvType,
              index: i
            });
            
            item.status = 'success';
            this.batchResults[i] = { success: true };
            this.resultStats.success++;
          } catch (error) {
            item.status = 'error';
            this.batchResults[i] = { success: false, error: error.message };
            this.resultStats.failed++;
          }
          
          this.processedCount++;
          
          // 添加短暂延迟，避免请求过于频繁
          await new Promise(resolve => setTimeout(resolve, 100));
        }
        
        this.batchLoading = false;
        this.$message.success(`批量上报完成，成功: ${this.resultStats.success}, 失败: ${this.resultStats.failed}`);
      },
      
      // 发送请求
      async sendRequest({ creativeId, requestId, cvType, index = -1 }) {
        const time = Date.now().toString();
        const nonce2 = Math.floor(Math.random() * 100000000000).toString();
        const token = '050882462da615ea58cd1338327c01ab9c572e42e6b97c3df14f9a335547de5f';
        const adid = '96f898521c3c57659e00';
        
        const url = `api/openapi/v1/advertiser/behavior/upload?access_token=${token}&timestamp=${time}&nonce=${nonce2}&advertiser_id=${adid}`;
        
        const payload = {
          srcId: 'ds-202308150901',
          pageUrl: 'https://www.baidu.com',
          srcType: 'Web',
          dataList: {
            cvTime: time,
            requestId,
            cvType,
            creativeId
          }
        };
        
        const response = await this.$axios.post(url, payload);
        
        if (response.data.code !== 0) {
          throw new Error(response.data.message || '上报失败');
        }
        
        return response.data;
      },
      
      // 清空批量数据
      clearBatchData() {
        this.parsedData = [];
        this.uploadedFileName = '';
        this.batchData.cvType = '';
        this.batchResults = [];
        this.resultStats = { total: 0, success: 0, failed: 0 };
      },
      
      // 获取批量状态
      getBatchStatus(index) {
        if (index === this.currentProcessingIndex) return 'warning';
        const item = this.parsedData[index];
        if (!item) return 'info';
        return item.status === 'success' ? 'success' : item.status === 'error' ? 'danger' : 'info';
      },
      
      // 获取批量状态文本
      getBatchStatusText(index) {
        if (index === this.currentProcessingIndex) return '处理中';
        const item = this.parsedData[index];
        if (!item) return '待处理';
        return item.status === 'success' ? '成功' : item.status === 'error' ? '失败' : '待处理';
      }
    }
  };
  </script>
  
  <style scoped>
  .info{
    width: 100%;
    /* padding: 20px; */
    background: white;
  }
  .data-report-container {
    max-width: 1000px;
    margin: 0 auto;
    padding: 20px;
    background: #fff;
    border-radius: 8px;
    box-shadow: 0 2px 12px rgba(0, 0, 0, 0.1);
  }
  
  .header {
    text-align: center;
    margin-bottom: 30px;
    padding-bottom: 20px;
    /* border-bottom: 1px solid #ebeef5; */
  }
  
  .title {
    color: #303133;
    margin-bottom: 8px;
    font-size: 24px;
  }
  
  .subtitle {
    color: #909399;
    font-size: 14px;
  }
  
  .tabs-container {
    margin: 0 -20px;
  }
  
  .single-report {
    padding: 20px;
  }
  
  .report-form {
    max-width: 500px;
    margin: 0 auto;
  }
  
  .type-select {
    width: 100%;
  }
  
  .submit-btn {
    width: 100%;
    height: 40px;
    font-size: 16px;
  }
  
  .batch-report {
    padding: 20px;
  }
  
  .upload-section {
    text-align: center;
    margin-bottom: 30px;
    padding: 20px;
    border: 2px dashed #dcdfe6;
    border-radius: 6px;
    background: #fafafa;
  }
  
  .file-info {
    margin-top: 15px;
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 15px;
  }
  
  .data-count {
    color: #67c23a;
    font-weight: 500;
  }
  
  .type-select-section {
    margin-bottom: 20px;
  }
  
  .data-preview {
    margin-bottom: 20px;
  }
  
  .data-preview h4 {
    margin-bottom: 15px;
    color: #606266;
  }
  
  .batch-actions {
    text-align: center;
    margin-bottom: 20px;
  }
  
  .batch-btn {
    width: 200px;
    height: 40px;
    font-size: 16px;
  }
  
  .progress-section {
    margin-top: 20px;
  }
  
  .progress-text {
    text-align: center;
    margin-top: 10px;
    color: #606266;
  }
  
  .result-stats {
    margin-top: 30px;
  }
  
  .stats-content {
    display: flex;
    justify-content: space-around;
    padding: 20px 0;
  }
  
  .stat-item {
    text-align: center;
  }
  
  .stat-label {
    display: block;
    color: #909399;
    margin-bottom: 5px;
  }
  
  .stat-value {
    display: block;
    font-size: 24px;
    font-weight: bold;
  }
  
  .stat-value.success {
    color: #67c23a;
  }
  
  .stat-value.error {
    color: #f56c6c;
  }
  
  .upload-demo {
    margin: 0 auto;
    max-width: 400px;
  }
  
  .el-icon-upload {
    font-size: 67px;
    color: #c0c4cc;
    margin: 40px 0 16px;
    line-height: 50px;
  }
  
  .el-upload__text {
    color: #606266;
    font-size: 14px;
    text-align: center;
  }
  
  .el-upload__tip {
    color: #909399;
    font-size: 12px;
    margin-top: 10px;
  }
  </style>


// WEBPACK FOOTER //
// src/components/vivo/reportform.vue